import { useContext } from "react";

import { IApiArgument, IFormInput } from "config";
import { DynamicFormContext } from "containers/DynamicForm/DynamicFormContext";
import { encrypt } from "utils/encrypt";

import parsePhoneNumberFromString from "libphonenumber-js";
import { find, get, isObjectLike, isEmpty, isNil, toString, trim } from "lodash-es";

export const useApiArgument = () => {
  const { inputs, mutationResponses } = useContext(DynamicFormContext);

  const getArgumentSource = (apiArgument: IApiArgument) => {
    const { source, sourceType } = apiArgument;
    const formInput = find(inputs, ["key", source]);
    switch (sourceType) {
      case "input":
        return sanitizeInputValue(apiArgument, formInput);
      case "response":
        return get(mutationResponses.current, source) || null;
      case "static":
        return sanitizeByArgumentType(source, apiArgument);
      default:
        return null;
    }
  };

  return getArgumentSource;
};

/*
  Sanitize input values on request payload to server
  - empty input values are returned as null
  - default values by MutationPayloadName type
  - transform input values by MutationPayloadName type
 */
const sanitizeInputValue = (argument: IApiArgument, input?: IFormInput): IFormInput["value"] => {
  const isEmptyValue = isNil(input?.value) || (isObjectLike(input?.value) && isEmpty(input?.value));

  // Default blobIds to empty array if empty or value is not an array
  if (argument.name === "blobIds") {
    if (isEmptyValue || !Array.isArray(input?.value)) {
      return [];
    } else {
      return input?.value;
    }
  }

  // Default countryCode to "1" US
  if (argument.name === "countryCode") {
    return isEmptyValue ? "1" : input?.value;
  }

  // Sanitize phone number country code prefix
  if (!isEmptyValue && argument.name === "phoneNumber") {
    const parsed = parsePhoneNumberFromString(String(input?.value ?? ""), "US");
    return parsed?.nationalNumber ? String(parsed.nationalNumber) : input?.value;
  }

  if (input?.encryptOutput) {
    const encryptedValue = encrypt(String(input.value));

    return encryptedValue;
  }

  if (!input || isEmptyValue) {
    return null;
  } else {
    return sanitizeInputByArgumentType(argument, input);
  }
};

export const sanitizeByArgumentType = (
  value: unknown,
  { type }: IApiArgument
): IFormInput["value"] => {
  switch (type) {
    case "boolean":
      if (value === "true") {
        return true;
      } else if (value === "false") {
        return false;
      } else {
        return Boolean(value);
      }

    case "float":
      return parseFloat(toString(value));

    case "int":
      return parseInt(toString(value), 10);

    case "string":
      return trim(toString(value));

    default:
      return value as IFormInput["value"];
  }
};

const sanitizeInputByArgumentType = (argument: IApiArgument, input: IFormInput) => {
  const coercedValue = sanitizeByArgumentType(input.value, argument);

  switch (argument.type) {
    case "boolean":
      return coercedValue;

    case "float":
    case "int":
      if (typeof coercedValue === "number" && isFinite(coercedValue)) {
        return coercedValue;
      } else {
        return null;
      }

    case "string":
      if (coercedValue === "") {
        return input?.allowEmptySubmit ? "" : null;
      } else {
        return coercedValue;
      }

    default:
      return coercedValue;
  }
};
